import { Icon } from '@buggy/shared'
import { skipToken } from '@reduxjs/toolkit/query'
import { Button, Flex, Input, List, Spin } from 'antd'
import { debounce } from 'lodash-es'
import { FunctionComponent, useCallback, useState, type ReactNode } from 'react'
import useInfiniteScroll from 'react-infinite-scroll-hook'
import styled from 'styled-components'
import {
  useFetchPredictiveReportKolInvolvesQuery,
  useFetchPredictiveReportListInfiniteQuery,
} from '@/api/workspace-api/predictive-report-api'
import { BasicModal } from '@/components/common/antd/basic-modal'
import CardErrorCosmos from '@/components/common/card/card-error-cosmos'
import { NoData } from '@/components/kol/detail/no-data'
import type { SelectCollaborativeItemsForKolModalProps } from '@/components/predictive-report/modals/select-collaborative-items-for-kol-modal'
import useHandleInputComposition from '@/hooks/use-handle-input-composition'
import { useIntl } from '@/i18n/hooks/use-intl'
import type { PredictiveReportListRequest } from '@/types/schema/api/predictive-report'
import { toVoidFunction } from '@/utils/to-void-function'

interface AddKolsToPredictiveReportModalProps
  extends Pick<SelectCollaborativeItemsForKolModalProps, 'candidateList'> {
  onClickAdd: (reportId: number) => void
  onClose: () => void
}

const AddKolsToPredictiveReportModal: FunctionComponent<
  AddKolsToPredictiveReportModalProps
> = ({ candidateList = [], onClose, onClickAdd }) => {
  const { formatMessage, formatNumber } = useIntl()
  const { compositionEnd, handleInputComposition } = useHandleInputComposition()

  const [debouncedRequest, setDebouncedRequest] =
    useState<PredictiveReportListRequest>({
      name: '',
      page: 1,
    })

  const {
    currentData,
    isLoading: isLoadingPredictiveReportList,
    isFetching: isFetchingPredictiveReportList,
    isError,
    hasNextPage,
    fetchNextPage,
  } = useFetchPredictiveReportListInfiniteQuery(debouncedRequest)

  const isSelectSingleKol = candidateList.length === 1
  const kolIds = candidateList.map((kol) => kol.uuid)

  const {
    data: fetchPredictiveReportKolInvolvesResponse,
    isLoading: isLoadingPredictiveReportKolInvolves,
  } = useFetchPredictiveReportKolInvolvesQuery(
    isSelectSingleKol ? { kol_uuids: [kolIds[0]] } : skipToken,
  )

  const isLoading =
    isLoadingPredictiveReportList || isLoadingPredictiveReportKolInvolves

  const [sentryRef] = useInfiniteScroll({
    loading: isFetchingPredictiveReportList,
    hasNextPage: hasNextPage ?? false,
    onLoadMore: toVoidFunction(fetchNextPage),
    disabled: !hasNextPage,
  })

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const handleOnSearch = useCallback(
    debounce((keyword: string) => {
      setDebouncedRequest({
        page: 1,
        name: keyword,
      })
    }, 500),
    [],
  )

  const dataSource = isLoading
    ? []
    : (currentData?.pages
        .map((page) => {
          return page.data
        })
        .flat() ?? [])

  return (
    <StyledModal
      centered
      destroyOnClose
      open
      footer={null}
      getContainer={false}
      title={formatMessage({ id: 'predictive_report:add_kols_modal_title' })}
      width={520}
      onCancel={onClose}
    >
      <Input.Search
        {...handleInputComposition}
        allowClear
        placeholder={formatMessage({ id: 'general:table_search_button' })}
        size='small'
        onChange={(event) => {
          if (!compositionEnd) {
            return
          }

          handleOnSearch(event.target.value)
        }}
        onCompositionEnd={(event) => {
          handleInputComposition.onCompositionEnd?.(event)
          handleOnSearch(event.currentTarget.value)
        }}
        onSearch={handleOnSearch}
      />
      {isError ? (
        <Flex align='center' justify='center' style={{ height: '100%' }}>
          <CardErrorCosmos size='small' />
        </Flex>
      ) : (
        <List
          dataSource={dataSource}
          footer={((): ReactNode => {
            if (isLoading) {
              return null
            }

            if (isFetchingPredictiveReportList || hasNextPage) {
              return (
                <div ref={sentryRef}>
                  <Flex
                    align='center'
                    justify='center'
                    style={{ height: '50px' }}
                  >
                    <Spin />
                  </Flex>
                </div>
              )
            }

            return null
          })()}
          itemLayout='horizontal'
          loading={isLoading}
          locale={{ emptyText: <NoData /> }}
          renderItem={(report) => {
            const isKolInvolved =
              isSelectSingleKol &&
              fetchPredictiveReportKolInvolvesResponse?.data.some(
                (item) =>
                  item.uuid === kolIds[0] && item.ids.includes(report.id),
              )

            return (
              <List.Item
                actions={
                  isKolInvolved
                    ? [
                        <AddedText key='added'>
                          <Icon fontSize={16} name='check' />
                          {formatMessage({
                            id: 'project:add_kol_added_text',
                          })}
                        </AddedText>,
                      ]
                    : [
                        <Button
                          icon={<Icon name='plus-square' />}
                          key='add'
                          type='text'
                          onClick={() => {
                            onClickAdd(report.id)
                          }}
                        >
                          {formatMessage({
                            id: 'general:add_btn',
                          })}
                        </Button>,
                      ]
                }
              >
                <List.Item.Meta
                  description={`${formatNumber(report.kol_count)} ${formatMessage({ id: 'general:identity_kol' })}`}
                  title={report.name}
                />
              </List.Item>
            )
          }}
        />
      )}
    </StyledModal>
  )
}

const StyledModal = styled(BasicModal)`
  .ant-modal-body {
    display: flex;
    flex-direction: column;
    gap: 16px;
    height: 500px;

    .ant-list {
      overflow-y: auto;

      .ant-list-item-meta-title {
        color: ${({ theme }): string => theme.colors.text.primary};
        font-size: 16px;
        font-style: normal;
        font-weight: 400;
        line-height: 140%;
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
      }

      .ant-list-item-meta-description {
        color: ${({ theme }): string => theme.colors.text.tooltip};
        font-size: 14px;
        font-style: normal;
        font-weight: 400;
        line-height: 140%;
      }

      .ant-btn {
        padding: 0;
      }
    }
  }
`

const AddedText = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
  color: ${({ theme }): string => theme.colors.brand.primary};
  font-size: 14px;
  font-style: normal;
  font-weight: 400;
  line-height: 140%;
`

export default AddKolsToPredictiveReportModal
